import { useMutation } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { gql } from "__generated__/gql";
import { getUpdateOrCreateVariables } from "components/Shared/Forms/AddressFieldset";
import { getUpdateOrCreatePhoneNumberVariables } from "components/Shared/Forms/PhoneInput";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import type { RetailerFormFields } from "components/Shared/RetailerDirectory/Form";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertBrandId } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";

export const createMutation = gql(/* GraphQL */ `
  mutation CreateRetailer(
    $id: UUID!
    $data: RetailerInput!
    $isAddressUpdatedOrCreated: Boolean = false
    $addressId: UUID
    $addressData: AddressPatch
    $isPhoneNumberUpdatedOrCreated: Boolean = false
    $phoneNumberId: UUID
    $phoneNumberData: PhoneNumberPatch
    $retailerChannelIds: [UUID!]!
    $retailerRegionIds: [UUID!]!
    $distributionCenterIds: [UUID!]!
  ) {
    ...UpdateOrCreateAddress
    ...UpdateOrCreatePhoneNumber
    createRetailer(input: { retailer: $data }) {
      retailer {
        id
      }
    }
    ...UpdateRetailerManyToMany
  }
`);

export function useCreateRetailer(isAdmin = false) {
  const brandId = useCurrentBrandId();

  const [performCreate, { loading: isSaving }] = useMutation(createMutation, {
    context: isAdmin
      ? ADMIN_CONTEXT
      : brandId && getActiveBrandIdContext(brandId),
    update: (cache, result) => {
      const newRetailer = result.data?.createRetailer?.retailer;
      if (!newRetailer || !brandId) return;
      addNewRecordsWithFieldSpec({
        cache,
        baseFieldName: isAdmin ? "retailers" : "retailersForBrand",
        newObjs: [newRetailer],
        createdAtEnumPrefix: isAdmin ? "CREATED_AT" : "CUSTOM_CREATED_AT",
        directionsTuple: [true],
        getArgs: (existing) => (isAdmin ? existing : { ...existing, brandId }),
      });
    },
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: RetailerFormFields) => {
      if (!isAdmin) assertBrandId(brandId);

      const {
        headquartersAddress,
        headquartersPhoneNumber,
        channelIds,
        regionIds,
        distributionCenterIds,
        ...retailerData
      } = formData;

      const id = uuidv4();

      const addressVariables = getUpdateOrCreateVariables(
        undefined,
        headquartersAddress,
      );

      const phoneNumberVariables = getUpdateOrCreatePhoneNumberVariables(
        undefined,
        headquartersPhoneNumber,
      );

      return performCreate({
        variables: {
          id,
          data: {
            ...retailerData,
            id,
            forBrandId: isAdmin ? null : brandId,
            headquartersAddressId: addressVariables.addressId,
            headquartersPhoneNumberId: phoneNumberVariables.phoneNumberId,
          },
          ...addressVariables,
          ...phoneNumberVariables,
          retailerChannelIds: channelIds,
          retailerRegionIds: regionIds,
          distributionCenterIds,
        },
      });
    },
    {
      // processApolloError,
      successMessage: "Retailer created successfully.",
    },
  );

  return { brandId, handleValidSubmit, isSaving };
}
